/**
 * Initial-metadata write for a freshly created grid.
 *
 * After the on-chain `createGrid` call confirms and we know the numeric
 * gridId emitted by the manager, the wizard calls this helper to stash
 * the wizard's name / description / socials / theming picks into the
 * pool_metadata table. Writes are gated by `requireGridCreator` on the
 * server (see `app/api/grid/[ugm]/[gridId]/metadata/route.ts`), so this
 * call WILL fail with 403 if the caller's Privy token doesn't resolve
 * to the grid's on-chain `creator_id`.
 *
 * Intentionally plain fetch — no @tanstack/react-query wrapper — so it
 * can be awaited inline from `useLaunchGrid` with a single try/catch
 * and doesn't pollute the query cache with a one-shot write.
 */

import { THEMING_NONE_SENTINEL } from '@/lib/theming-validation';

import type { LaunchDraft } from './use-launch-wizard';

export interface WriteInitialGridMetadataArgs {
  /** UnifiedGridManager address the grid was created on. Lower-cased by
   *  the server, so no need to normalise here. */
  managerAddress: string;
  /** Numeric gridId emitted by the factory on create. */
  gridId: number | string | bigint;
  /** Frozen copy of the wizard's draft at confirm-time. */
  draft: LaunchDraft;
  /** Optional Privy access token. When provided it's sent as a Bearer
   *  header; when omitted we still send the request so local dev (where
   *  auth is skipped) keeps working. */
  accessToken?: string | null;
}

export interface WriteInitialGridMetadataResult {
  ok: boolean;
  /** Server-side error message, when `ok === false`. */
  error?: string;
}

/**
 * Build the JSON body the metadata POST expects, using the wizard draft
 * as input. Pulled out of the `fetch` call so the same payload can be
 * stashed in `pending-metadata` storage before the network attempt
 * (the retry path on subsequent mounts uses the persisted body so a
 * lost wizard session can still flush the user's picks).
 */
export function buildMetadataPayload(draft: LaunchDraft): Record<string, unknown> {
  // Identity "None" on either picker is a real user choice — persist the
  // `'none'` sentinel so the lobby renders no plinth / no skybox instead
  // of falling back to an adapter-derived default. The validator on the
  // API route accepts the sentinel as a recognised value; legacy boards
  // (column NULL / row missing) keep their adapter fallback unchanged.
  const skyboxId = draft.skyboxId ?? THEMING_NONE_SENTINEL;
  const plinthId = draft.plinthId ?? THEMING_NONE_SENTINEL;

  // Pull the small set of fields the wizard actually collects. Anything
  // missing at this point has an acceptable default on the server (name
  // falls back to `Grid #<id>`, symbol to `G<id>`).
  //
  // `imageUrl` lands here too now that the Identity step's `IdentityCard`
  // accepts a drag-drop upload — the post-launch Board step only attaches
  // adapters / yield, no token creation, so the wizard is the only
  // surface that writes the cover image at this stage. Empty string means
  // "no image picked"; the server keeps any pre-existing column value
  // because `setPoolMetadata` ignores `undefined` via `COALESCE`.
  //
  // `twitterHandle` (not `twitterUrl`) is now the canonical X field —
  // the metadata route enforces an OAuth-verified match in
  // `verified_x_handles` before persisting. The server reconstructs
  // `twitter_url = https://x.com/${handle}` for legacy readers.
  return {
    name: draft.name.trim() || undefined,
    description: draft.description?.trim() || undefined,
    imageUrl: draft.imageUrl?.trim() || undefined,
    twitterHandle: draft.twitterHandle?.trim() || undefined,
    telegramUrl: draft.socials.telegram?.trim() || undefined,
    websiteUrl: draft.socials.site?.trim() || undefined,
    boardPlinth: plinthId,
    boardSkybox: skyboxId,
  };
}

export async function writeInitialGridMetadata({
  managerAddress,
  gridId,
  draft,
  accessToken,
}: WriteInitialGridMetadataArgs): Promise<WriteInitialGridMetadataResult> {
  const numericId =
    typeof gridId === 'bigint' ? gridId.toString() : String(gridId);

  const payload = buildMetadataPayload(draft);

  return postMetadata({
    managerAddress,
    numericId,
    body: payload,
    accessToken,
  });
}

/**
 * Low-level metadata POST helper. Used both by the inline launch flow
 * (`writeInitialGridMetadata` above) and by the lobby's pending-metadata
 * retry path, which already has a serialised `body` from `localStorage`
 * and just needs the network call + retry logic. Splitting them keeps
 * the wizard's "draft → payload" coercion in one place while letting
 * the retry path stay agnostic of the wizard's draft shape.
 */
export async function postMetadata(args: {
  managerAddress: string;
  numericId: string;
  body: Record<string, unknown>;
  accessToken?: string | null;
}): Promise<WriteInitialGridMetadataResult> {
  const { managerAddress, numericId, body, accessToken } = args;
  const url = `/api/grid/${managerAddress.toLowerCase()}/${numericId}/metadata`;

  // Retry transient failures with exponential backoff. The dominant
  // failure modes immediately post-launch are:
  //   - 404 "Grid not found" because the indexer hasn't ingested the
  //     `GridCreated` event yet (server's RPC fallback usually closes
  //     this, but the RPC itself can also be slow on a cold cache).
  //   - 503 from Envio outages.
  //   - Network blips from the user's connection.
  // 4xx other than 404/429 are deterministic (validation, 403 wrong
  // creator) and not worth retrying. 5xx and the timing-window 404 are.
  const MAX_ATTEMPTS = 4;
  const BACKOFF_MS = [0, 1500, 4000, 9000];
  let lastError = 'Network error while saving metadata';

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (BACKOFF_MS[attempt] > 0) {
      await new Promise((r) => setTimeout(r, BACKOFF_MS[attempt]));
    }
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        return { ok: true };
      }

      let message = `HTTP ${res.status}`;
      try {
        const data = await res.json();
        if (data?.error || data?.message) {
          message = data.error || data.message;
        }
      } catch {
        // Non-JSON body — stick with the status line.
      }
      lastError = message;

      // Deterministic failures (auth, validation) — no point retrying.
      const isRetryable =
        res.status === 404 || res.status === 429 || res.status >= 500;
      if (!isRetryable) {
        return { ok: false, error: message };
      }
    } catch (err) {
      lastError =
        err instanceof Error ? err.message : 'Network error while saving metadata';
    }
  }

  return { ok: false, error: lastError };
}
